import {
  JobsiteCustomWorkerDataFieldInput,
  JobsiteOnboardingFieldInput,
  JobsiteOnboardingStepInput,
  OnboardingStepKey,
} from 'apollo/generated/client-operations';
import {
  FormInputTypes,
  getUpdateInputValueFunction,
  GridColSpan,
  TypedFormInputs,
} from 'components/form';
import { ShieldCheckIcon } from 'components/icons';
import { WorkerInfoField } from 'containers/jobsiteConfiguration/components/workerInfoField';
import { toggleBorderClasses } from 'containers/jobsiteConfiguration/helpers/utils';
import {
  CustomWorkerDataFieldInput,
  EditJobsiteConfigurationFormData,
  EditJobsiteConfigWorkerInfoField,
} from 'containers/jobsiteConfiguration/types';
import React from 'react';
import { DeepMap } from 'react-hook-form';
import { Editable } from 'types';
import { cn, ensureNonUndefinedFields, getTempId } from 'utils';
import { Jobsite } from './types';

const fieldDataTypeOptions = [
  {
    label: 'String',
    value: 'string',
  },
];

export const siteSpecificOrientationSectionInputs: TypedFormInputs<
  EditJobsiteConfigurationFormData['siteSpecificOrientation']
> = {
  stickerNumber: {
    element: FormInputTypes.CustomInput,
    label: 'Helmet sticker number',
    layout: [GridColSpan.SpanFull, toggleBorderClasses],
    elementProps: { customInput: WorkerInfoField },
  },
  customWorkerDataFields: {
    element: FormInputTypes.Repeater,
    elementProps: {
      shouldTrackChanges: true,
      addItemText: 'Add property',
      getNewItem: (): Editable<CustomWorkerDataFieldInput> => ({
        id: getTempId(),
        fieldDataType: fieldDataTypeOptions[0],
        fieldLabel: '',
        validationMessage: '',
        validationRegex: '',
        isRequired: false,
      }),
      itemClassName: cn('odin-grid odin-grid-cols-6 odin-gap-3'),
      removeItemPosition: 'inline',
      removeItemClassName: 'odin-mt-6',
    },
    layout: 'odin-col-span-8',
    children: ensureNonUndefinedFields<
      TypedFormInputs<Editable<CustomWorkerDataFieldInput>>
    >({
      // RHF v6 (web-app) doesn't save `id` field without an input
      id: {
        element: FormInputTypes.Field,
        label: 'Id',
        layout: 'odin-hidden',
      },
      // RHF v6 (web-app) doesn't save `changeType` field without an input
      // use CustomInput because the Field component doesn't support undefined as `defaultValue`
      changeType: {
        element: FormInputTypes.CustomInput,
        label: '',
        elementProps: { customInput: React.forwardRef(() => null) },
        layout: 'odin-hidden',
      },
      isRequired: {
        element: FormInputTypes.Checkbox,
        label: 'Make required',
        layout: [
          GridColSpan.SpanFull,
          'odin-flex',
          'odin-justify-end',
          'odin-mb-[-20px]',
        ],
      },
      fieldDataType: {
        element: FormInputTypes.Select,
        label: 'Type',
        elementProps: {
          clearToNull: true,
          isClearable: false,
          options: fieldDataTypeOptions,
        },
        layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
        validation: { required: true },
      },
      fieldLabel: {
        element: FormInputTypes.Field,
        label: 'Name',
        layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
        validation: {
          required: true,
        },
      },
      validationRegex: {
        element: FormInputTypes.Field,
        label: 'Validation regex',
        layout: [GridColSpan.SpanFull],
        elementProps: {
          icon: ShieldCheckIcon,
        },
        validation: {
          required: false,
          validate: (validationRegex: string): string | null => {
            try {
              // [we are compiling to check regex validity]
              // eslint-disable-next-line no-new
              new RegExp(validationRegex);
              return null;
            } catch {
              return 'Not a valid regular expression.';
            }
          },
        },
      },
      validationMessage: {
        element: FormInputTypes.Field,
        label: 'Validation message',
        layout: [GridColSpan.SpanFull],
        elementProps: {
          icon: ShieldCheckIcon,
        },
        validation: {
          required: false,
        },
      },
    }),
  },
};

export const getSiteSpecificOrientationSectionDefaultValues = (
  jobsite: Jobsite,
): EditJobsiteConfigurationFormData['siteSpecificOrientation'] => {
  const siteSpecificOrientationStep = jobsite?.onboardingModule?.steps?.find(
    (s) => s.key === OnboardingStepKey.SiteSpecificOrientation,
  );
  const { fields } = siteSpecificOrientationStep ?? {};

  const getSiteSpecificOrientationField = (
    fieldKey: keyof Omit<
      EditJobsiteConfigurationFormData['siteSpecificOrientation'],
      'customWorkerDataFields'
    >,
  ): EditJobsiteConfigWorkerInfoField => {
    const { isRequired, isHidden, isEnforced } =
      fields?.find((f) => f.key === fieldKey) ?? {};
    return {
      isRequired: !!isRequired,
      isHidden: !!isHidden,
      isEnforced: !!isEnforced,
    };
  };

  return {
    stickerNumber: getSiteSpecificOrientationField('stickerNumber'),
    customWorkerDataFields:
      jobsite?.jobsiteCustomWorkerDataFields?.edges?.map(
        ({ node }): Editable<CustomWorkerDataFieldInput> => ({
          id: node.id,
          changeType: null,
          fieldDataType: {
            label: node.fieldDataType,
            value: node.fieldDataType,
          },
          fieldLabel: node.fieldLabel,
          validationRegex: node.validationRegex ?? '',
          validationMessage: node.validationMessage ?? '',
          isRequired: node.isRequired,
        }),
      ) ?? [],
  };
};

type SiteSpecificOrientationSectionUpdateInput = {
  siteSpecificOrientationStep: JobsiteOnboardingStepInput;
  jobsiteCustomWorkerDataFields: JobsiteCustomWorkerDataFieldInput[];
};

export const getSiteSpecificOrientationSectionUpdateInput = (
  siteSpecificOrientation: EditJobsiteConfigurationFormData['siteSpecificOrientation'],
  dirtyFields: DeepMap<
    EditJobsiteConfigurationFormData['siteSpecificOrientation'],
    true
  >,
): SiteSpecificOrientationSectionUpdateInput => {
  const getUpdateInputValue = getUpdateInputValueFunction(
    siteSpecificOrientation,
    dirtyFields,
  );

  const stepFieldKeys = (
    Object.keys(siteSpecificOrientation) as Array<
      keyof typeof siteSpecificOrientation
    >
  ).filter((key) => key !== 'customWorkerDataFields');

  const fields: JobsiteOnboardingFieldInput[] = stepFieldKeys
    .map((key) => ({
      key,
      ...getUpdateInputValue(key),
    }))
    .filter(
      ({ isRequired, isHidden }) =>
        isRequired !== undefined || isHidden !== undefined,
    );

  const customWorkerDataFieldsInput = getUpdateInputValue(
    'customWorkerDataFields',
  )
    ?.filter((f) => f.changeType)
    .map(
      (f): JobsiteCustomWorkerDataFieldInput => ({
        id: f.id,
        fieldDataType: f.fieldDataType?.value,
        fieldLabel: f.fieldLabel,
        validationMessage: f.validationMessage || null,
        validationRegex: f.validationRegex || null,
        isRequired: f.isRequired ?? false,
        changeType: f.changeType,
      }),
    );

  return {
    siteSpecificOrientationStep: fields.length
      ? {
          key: OnboardingStepKey.SiteSpecificOrientation,
          fields,
        }
      : undefined,
    jobsiteCustomWorkerDataFields: customWorkerDataFieldsInput ?? [],
  };
};
